import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../Header';

/**
 * UI / UX STUDIO PAGE
 * Theme: 60% Aqua Haze (#F0F4F8) | 30% River Bed (#4A5568) | 10% Cerulean Blue (#2B6CB0)
 * Headline: High-Contrast Authority (#020617)
 */
const SERVICES = [
  { title: 'User Research', desc: 'Deep diving into user behavior, mental models, and pain points.' },
  { title: 'Wireframing', desc: 'Architecting the structural foundation of your digital product.' },
  { title: 'Prototyping', desc: 'Building interactive high-fidelity models for real-world testing.' },
  { title: 'Visual Design', desc: 'Crafting pixel-perfect interfaces that define your brand identity.' },
  { title: 'Usability Testing', desc: 'Gathering data-driven insights to refine the user journey.' },
  { title: 'Design Systems', desc: 'Creating scalable, consistent component libraries for developers.' }
];

const PROCESS_STEPS = [
  { step: '01', title: 'Discovery', desc: 'Mapping goals and user needs.' },
  { step: '02', title: 'Structure', desc: 'Information architecture and flow.' },
  { step: '03', title: 'Interface', desc: 'Visual execution and interaction.' },
  { step: '04', title: 'Validation', desc: 'Testing and iterative refinement.' }
];

const UIUX = () => {
  return (
    <div className="min-h-screen bg-[#F0F4F8] text-[#4A5568] font-sans">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 bg-white border-b border-[#F0F4F8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block px-5 py-2 mb-8 text-[10px] font-black tracking-[0.3em] uppercase bg-[#020617] text-white rounded-full">
              Experience Design Studio
            </span>
            <h1 className="text-5xl md:text-8xl font-black mb-6 tracking-tighter text-[#020617] uppercase">
              UI <span className="text-[#2B6CB0]">/</span> UX <br/> Strategic Design
            </h1>
            <p className="text-xl text-[#4A5568] max-w-2xl mx-auto mb-12 leading-relaxed font-medium">
              We eliminate friction between human intent and digital execution 
              through obsessive attention to visual psychology and technical detail.
            </p>
            <Link to="/contact" className="inline-block bg-[#2B6CB0] text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-[#020617] transition-all shadow-lg shadow-[#2B6CB0]/20">
              Start Your Project
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Image Spotlight */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-32">
            <div>
              <h2 className="text-4xl md:text-5xl font-black mb-8 tracking-tighter text-[#020617] uppercase leading-none">
                Form Follows <br/> Feeling
              </h2>
              <p className="text-lg text-[#4A5568] mb-10 leading-relaxed font-medium">
                Design isn't just how it looks; it's how it works. We focus on the psychology of movement and the clarity of information to create intuitive digital products.
              </p>
              <ul className="space-y-6">
                {['Intentionality', 'Accessibility', 'Scalability'].map((item, i) => (
                  <li key={i} className="flex items-center gap-6 group">
                    <span className="text-[#2B6CB0] font-black text-sm">0{i+1}</span>
                    <span className="text-sm font-black uppercase tracking-widest text-[#020617] group-hover:translate-x-2 transition-transform">
                      {item}
                    </span>
                    <div className="flex-1 h-[1px] bg-[#4A5568]/10"></div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#2B6CB0]/5 rounded-3xl -rotate-2"></div>
              <img
                src="https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?auto=format&fit=crop&q=80&w=800"
                alt="UI UX Design"
                className="relative w-full h-[500px] object-cover rounded-2xl shadow-2xl border-8 border-white grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>

          {/* Capabilities Grid */}
          <div className="mb-32">
            <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter text-[#020617] uppercase">
              Core <span className="text-[#2B6CB0]">Capabilities</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((service, index) => (
                <div key={index} className="bg-white p-10 rounded-2xl border border-[#F0F4F8] shadow-sm hover:border-[#2B6CB0]/30 transition-all group">
                  <div className="w-10 h-1 bg-[#2B6CB0] mb-6 group-hover:w-full transition-all duration-500"></div>
                  <h3 className="text-sm font-black mb-4 text-[#020617] uppercase tracking-widest leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed font-medium">
                    {service.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Process Section */}
          <div className="mb-32">
            <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter text-[#020617] uppercase">
              Our <span className="text-[#2B6CB0]">Process</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PROCESS_STEPS.map((item) => (
                <div key={item.step} className="bg-white/50 p-8 rounded-2xl border border-white relative group overflow-hidden">
                  <span className="absolute -right-4 -top-4 text-7xl font-black text-[#020617]/5 group-hover:text-[#2B6CB0]/10 transition-colors">
                    {item.step}
                  </span>
                  <h3 className="text-sm font-black mb-3 text-[#020617] uppercase tracking-widest relative z-10">{item.title}</h3>
                  <p className="text-[#4A5568] text-xs font-medium leading-relaxed relative z-10">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA Card */}
          <div className="text-center bg-[#020617] p-16 rounded-[3rem] shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#2B6CB0] opacity-10 rounded-full -mr-32 -mt-32"></div>
            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter text-white uppercase leading-none">
              Ready to Build <br/> <span className="text-[#2B6CB0]">The Future?</span>
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto font-medium">
              Let's create a digital experience that captures your brand and resonates with your users.
            </p>
            <div className="flex flex-wrap gap-4 justify-center relative z-10">
              <Link to="/contact" className="bg-[#2B6CB0] text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all">
                Get Started Now
              </Link>
              <Link to="/portfolio" className="border-2 border-white/20 text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white hover:text-[#020617] transition-all">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UIUX;